import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Sparkle, X, ArrowsClockwise, CheckCircle, Copy, PencilSimple, Info,
} from "@phosphor-icons/react";

/**
 * ATLAS Draft Modal — Fase 5.3
 * Genera propuestas editables (título / descripción) usando SOLO los datos
 * capturados de la propiedad. NUNCA guarda ni publica: el asesor decide y
 * aplica manualmente el borrador con onApply(text).
 */
export function AtlasDraftModal({
  open,
  onClose,
  kind,            // "title" | "description"
  propertyData,    // objeto con los campos del formulario capturados hasta el momento
  onApply,         // (text) => void — llena el campo del formulario
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [drafts, setDrafts]   = useState([]);   // [{id, text}]
  const [fallback, setFallback] = useState(false);
  const [applyingIndex, setApplyingIndex] = useState(-1);

  const heading = kind === "title"
    ? "Crear título con ATLAS"
    : "Crear descripción con ATLAS";
  const helper = kind === "title"
    ? "ATLAS propone 3 títulos comerciales usando solo los datos que ya capturaste. Edita el borrador y aplícalo cuando estés listo."
    : "ATLAS propone descripciones cortas usando solo los datos que ya capturaste. Edita el borrador y aplícalo cuando estés listo.";

  const generate = async () => {
    setLoading(true); setError(""); setFallback(false); setDrafts([]);
    try {
      const { data } = await api.post("/atlas/generate", {
        kind,
        property_data: propertyData || {},
      });
      const items = (data?.drafts || []).map((t, i) => ({ id: i, text: String(t || "") }));
      setDrafts(items);
      setFallback(Boolean(data?.fallback));
    } catch (err) {
      setError(formatApiError(err, "ATLAS no pudo preparar los borradores en este momento."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);

  if (!open) return null;

  const updateDraft = (idx, text) => {
    setDrafts(prev => prev.map((d, i) => i === idx ? { ...d, text } : d));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Texto copiado");
    } catch { toast.error("No se pudo copiar el texto"); }
  };

  const applyDraft = (idx, text) => {
    setApplyingIndex(idx);
    onApply?.(text);
    toast.success(kind === "title" ? "Título aplicado" : "Descripción aplicada");
    // Small delay so the user sees the confirmation state before closing
    setTimeout(() => { setApplyingIndex(-1); onClose?.(); }, 250);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid={`atlas-draft-modal-${kind}`}
    >
      <div className="w-full sm:max-w-2xl bg-[#0F0F0F] border border-[#C8A24A]/30 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#C8A24A]/15">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> ATLAS · Asistente comercial
            </p>
            <h2 className="font-heading text-xl sm:text-2xl mt-1 tracking-tight" data-testid={`atlas-draft-title-${kind}`}>
              {heading}
            </h2>
            <p className="text-xs text-[#C8A24A]/70 mt-1.5 leading-relaxed">{helper}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#C8A24A]/60 hover:text-[#C8A24A] p-1 flex-shrink-0"
            data-testid={`atlas-draft-close-${kind}`}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="space-y-3" data-testid={`atlas-draft-loading-${kind}`}>
              {[0, 1, 2].map(i => (
                <div key={i} className="rounded-xl border border-[#C8A24A]/15 bg-[#151515] p-4 animate-pulse">
                  <div className="h-3 w-24 bg-[#0A0A0A] rounded mb-3" />
                  <div className="h-16 bg-[#0A0A0A] rounded" />
                </div>
              ))}
              <p className="text-xs text-[#C8A24A]/60 text-center">Preparando propuestas...</p>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-center" data-testid={`atlas-draft-error-${kind}`}>
              <p className="text-sm text-red-300">{error}</p>
              <Button
                onClick={generate}
                variant="outline"
                size="sm"
                className="mt-3 h-9"
                data-testid={`atlas-draft-retry-${kind}`}
              >
                <ArrowsClockwise size={14} className="mr-1.5" /> Reintentar
              </Button>
            </div>
          )}

          {!loading && !error && drafts.length === 0 && (
            <div className="rounded-xl border border-dashed border-[#C8A24A]/25 bg-[#0A0A0A] p-6 text-center" data-testid={`atlas-draft-empty-${kind}`}>
              <p className="text-sm text-[#C8A24A]/70">
                ATLAS no pudo preparar propuestas. Intenta completar más datos de la propiedad (tipo, ciudad, precio) y vuelve a intentar.
              </p>
            </div>
          )}

          {!loading && !error && drafts.length > 0 && (
            <>
              {fallback && (
                <div className="rounded-lg border border-[#C8A24A]/25 bg-[#C8A24A]/5 p-3 text-xs text-[#C8A24A]/80 flex items-start gap-2" data-testid={`atlas-draft-fallback-${kind}`}>
                  <Info size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <span>Preparamos borradores básicos con los datos disponibles. Puedes editarlos libremente antes de aplicarlos.</span>
                </div>
              )}

              <ul className="space-y-3" data-testid={`atlas-draft-list-${kind}`}>
                {drafts.map((d, idx) => (
                  <li
                    key={d.id}
                    className="rounded-xl border border-[#C8A24A]/20 bg-[#151515] p-3 sm:p-4"
                    data-testid={`atlas-draft-item-${kind}-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs uppercase tracking-widest font-bold text-[#C8A24A]/80">
                        Propuesta {idx + 1}
                      </span>
                      <PencilSimple size={11} className="text-[#C8A24A]/50" />
                      <span className="text-xs text-[#C8A24A]/50 italic">editable</span>
                    </div>
                    <Textarea
                      value={d.text}
                      onChange={(e) => updateDraft(idx, e.target.value)}
                      rows={kind === "description" ? 5 : 2}
                      className="bg-[#0A0A0A] border-[#C8A24A]/20 text-sm resize-y"
                      data-testid={`atlas-draft-text-${kind}-${idx}`}
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button
                        onClick={() => applyDraft(idx, d.text)}
                        disabled={!d.text.trim() || applyingIndex === idx}
                        className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-9 px-4"
                        data-testid={`atlas-draft-apply-${kind}-${idx}`}
                      >
                        {applyingIndex === idx ? (
                          <><CheckCircle size={14} weight="fill" className="mr-1.5" /> Aplicado</>
                        ) : (
                          <><CheckCircle size={14} className="mr-1.5" /> Usar este borrador</>
                        )}
                      </Button>
                      <Button
                        onClick={() => copyToClipboard(d.text)}
                        variant="outline"
                        size="sm"
                        className="h-9"
                        data-testid={`atlas-draft-copy-${kind}-${idx}`}
                      >
                        <Copy size={13} className="mr-1.5" /> Copiar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#C8A24A]/15 flex items-center justify-between gap-3">
          <p className="text-xs text-[#C8A24A]/50 leading-tight">
            ATLAS propone; tú decides. Nada se guarda ni se publica sin tu confirmación.
          </p>
          <Button
            onClick={generate}
            variant="outline"
            size="sm"
            disabled={loading}
            className="h-9 flex-shrink-0"
            data-testid={`atlas-draft-regenerate-${kind}`}
          >
            <ArrowsClockwise size={14} className="mr-1.5" /> Regenerar
          </Button>
        </div>
      </div>
    </div>
  );
}
